import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Search, Download, ArrowUpRight, ArrowDownLeft, Filter, Calendar } from 'lucide-react';
import API_BASE_URL from '../../config/api';


const WalletHistory = () => {
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterType, setFilterType] = useState('all'); // all, credit, debit
    const [walletFilter, setWalletFilter] = useState('main_wallet'); // main_wallet, shopping_wallet
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const fetchHistory = useCallback(async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('authToken');
            const url = new URL(`${API_BASE_URL}/admin/get_wallet_history.php`);
            if (fromDate) url.searchParams.append('from', fromDate);
            if (toDate) url.searchParams.append('to', toDate);
            url.searchParams.append('_t', Date.now());

            const response = await fetch(url.toString(), {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setTransactions(data.transactions);
            }
        } catch (error) {
            console.error("Failed to fetch wallet history", error);
        } finally {
            setLoading(false);
        }
    }, [fromDate, toDate]);

    useEffect(() => {
        fetchHistory();
    }, [walletFilter, fetchHistory]);

    const filteredTransactions = transactions.filter(t => {
        const matchesSearch =
            (t.user_name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (t.user_code || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
            (t.email || '').toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = filterType === 'all' || t.transaction_type === filterType;

        let matchesWallet = false;
        if (walletFilter === 'all') {
            matchesWallet = true;
        } else if (walletFilter === 'shopping_wallet') {
            matchesWallet = t.wallet_type === 'shopping_wallet' || t.wallet_type === 'shopping';
        } else {
            matchesWallet = t.wallet_type === walletFilter;
        }

        return matchesSearch && matchesFilter && matchesWallet;
    });

    return (
        <Layout>
            <div className="space-y-6">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">Wallet History</h1>
                        <p className="text-gray-600 mt-1">View all user wallet transactions and movements</p>
                    </div>
                    {/* Export Button Placeholder */}
                    <button className="flex items-center space-x-2 px-4 py-2 bg-white border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition-colors">
                        <Download size={18} />
                        <span>Export CSV</span>
                    </button>
                </div>

                {/* Filters & Search */}
                {/* Tabs */}
                <div className="flex space-x-1 bg-white p-1 rounded-xl border border-gray-100 w-fit shadow-sm mb-4">
                    {['main_wallet', 'shopping_wallet'].map((tab) => (
                        <button
                            key={tab}
                            onClick={() => setWalletFilter(tab)}
                            className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition-all capitalize ${walletFilter === tab
                                ? 'bg-blue-600 text-white shadow-md'
                                : 'text-gray-600 hover:bg-gray-50'
                                }`}
                        >
                            {tab.replace('_', ' ')}
                        </button>
                    ))}
                </div>

                <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 flex flex-col lg:flex-row gap-4 justify-between items-center">
                    <div className="flex flex-wrap items-center gap-3">
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                            <Calendar size={16} className="text-gray-400" />
                            <input
                                type="date"
                                value={fromDate}
                                onChange={(e) => setFromDate(e.target.value)}
                                className="bg-transparent text-xs font-black focus:outline-none cursor-pointer"
                            />
                        </div>
                        <span className="text-gray-300 text-xs font-black">to</span>
                        <div className="flex items-center gap-2 bg-gray-50 px-3 py-2 rounded-xl border border-gray-100 cursor-pointer" onClick={(e) => e.currentTarget.querySelector('input').showPicker()}>
                            <Calendar size={16} className="text-gray-400" />
                            <input
                                type="date"
                                value={toDate}
                                onChange={(e) => setToDate(e.target.value)}
                                className="bg-transparent text-xs font-black focus:outline-none cursor-pointer"
                            />
                        </div>

                        <button
                            onClick={fetchHistory}
                            className="px-4 py-2 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-all"
                        >
                            Filter
                        </button>

                        <div className="h-8 w-px bg-gray-100 hidden lg:block"></div>

                        <Filter size={18} className="text-gray-500" />
                        <select
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                            className="px-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none bg-gray-50 text-xs font-bold"
                        >
                            <option value="all">All Types</option>
                            <option value="credit">Credits (Income)</option>
                            <option value="debit">Debits (Withdrawals)</option>
                        </select>
                    </div>

                    <div className="relative w-full lg:w-96">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                        <input
                            type="text"
                            placeholder="Search by User ID, Name, or Email..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none text-sm font-medium"
                        />
                    </div>
                </div>

                {/* Table */}
                {/* Mobile Grid View */}
                <div className="grid grid-cols-1 gap-4 lg:hidden">
                    {loading ? (
                        <div className="flex justify-center p-12">
                            <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                        </div>
                    ) : filteredTransactions.length === 0 ? (
                        <div className="p-8 text-center bg-white rounded-2xl border border-dashed text-gray-500 font-bold uppercase tracking-widest text-xs">No transactions found</div>
                    ) : (
                        filteredTransactions.map(t => (
                            <div key={t.id} className="bg-white p-4 rounded-2xl border border-gray-100 shadow-sm flex flex-col gap-3">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <div className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-1">User</div>
                                        <div className="font-black text-gray-900">{t.user_name || 'N/A'}</div>
                                        <div className="text-xs text-gray-500">ID: {t.user_code}</div>
                                    </div>
                                    <div className="flex flex-col items-end gap-1">
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${t.transaction_type === 'credit' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                                            {t.transaction_type}
                                        </span>
                                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider ${(t.wallet_type === 'shopping_wallet' || t.wallet_type === 'shopping') ? 'bg-purple-50 text-purple-700 border border-purple-200' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}>
                                            {(t.wallet_type === 'shopping_wallet' || t.wallet_type === 'shopping') ? 'Shopping' : 'Main'}
                                        </span>
                                    </div>
                                </div>

                                <div className="bg-gray-50 rounded-xl p-3 border border-gray-100">
                                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Transaction Detail</div>
                                    <div className="text-sm text-gray-700 font-medium">{t.description || '-'}</div>
                                </div>

                                <div className="grid grid-cols-2 gap-4 py-2 border-y border-gray-50">
                                    <div>
                                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Amount</div>
                                        <div className={`font-black ${t.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                                            {t.transaction_type === 'credit' ? '+' : '-'} ₹{parseFloat(t.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                        </div>
                                    </div>
                                    <div>
                                        <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-1">Balance</div>
                                        <div className="font-bold text-gray-700">₹{parseFloat(t.balance_after).toLocaleString('en-IN', { minimumFractionDigits: 2 })}</div>
                                    </div>
                                </div>

                                <div className="flex justify-between items-center pt-1">
                                    <div className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">Date & Time</div>
                                    <div className="text-xs font-bold text-gray-600">
                                        {new Date(t.created_at).toLocaleDateString()} at {new Date(t.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>

                {/* Desktop Table */}
                <div className="hidden lg:block bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full">
                            <thead className="bg-gray-50 border-b border-gray-200">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">User Details</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Transaction</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Wallet</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Amount</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Balance</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Date</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                            <div className="flex justify-center items-center gap-2">
                                                <div className="w-5 h-5 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                                Loading history...
                                            </div>
                                        </td>
                                    </tr>
                                ) : filteredTransactions.length === 0 ? (
                                    <tr>
                                        <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                                            No transactions found matching your criteria.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredTransactions.map((t) => (
                                        <tr key={t.id} className="hover:bg-gray-50 transition-colors">
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex flex-col">
                                                    <span className="font-semibold text-gray-900">{t.user_name || 'N/A'}</span>
                                                    <span className="text-xs text-gray-500">ID: {t.user_code}</span>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                                                {t.email}
                                            </td>
                                            <td className="px-6 py-4">
                                                <div className="flex items-center gap-2">
                                                    <div className={`p-1.5 rounded-full ${t.transaction_type === 'credit' ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                                                        {t.transaction_type === 'credit' ? <ArrowDownLeft size={14} /> : <ArrowUpRight size={14} />}
                                                    </div>
                                                    <div className="flex flex-col">
                                                        <span className={`text-sm font-medium ${t.transaction_type === 'credit' ? 'text-green-700' : 'text-red-700'} capitalize`}>
                                                            {t.transaction_type}
                                                        </span>
                                                        <span className="text-xs text-gray-500 max-w-xs truncate" title={t.description}>
                                                            {t.description || '-'}
                                                        </span>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4">
                                                <span className={`px-2.5 py-0.5 rounded text-xs font-semibold border ${(t.wallet_type === 'shopping_wallet' || t.wallet_type === 'shopping')
                                                    ? 'bg-purple-50 text-purple-700 border-purple-200'
                                                    : 'bg-blue-50 text-blue-700 border-blue-200'
                                                    }`}>
                                                    {(t.wallet_type === 'shopping_wallet' || t.wallet_type === 'shopping') ? 'Shopping' : 'Main'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right">
                                                <span className={`font-bold ${t.transaction_type === 'credit' ? 'text-green-600' : 'text-red-600'}`}>
                                                    {t.transaction_type === 'credit' ? '+' : '-'} ₹ {parseFloat(t.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-700 font-medium">
                                                ₹ {parseFloat(t.balance_after).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                {new Date(t.created_at).toLocaleDateString()}
                                                <div className="text-xs">{new Date(t.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</div>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    <div className="bg-gray-50 px-6 py-4 border-t border-gray-200">
                        <div className="text-xs text-gray-500">
                            Showing {filteredTransactions.length} transaction(s)
                        </div>
                    </div>
                </div>
            </div>
        </Layout>
    );
};

export default WalletHistory;


